import { FC, useContext, useMemo, useState } from 'react';
import { truncate } from 'lodash-es';
import { AiOutlineEye } from 'react-icons/ai';
import { Icon, Stack, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { useApplicationMessages } from '~/core/shared/api/middle-office/application';
import { DateTimeDisplay } from '~/core/shared/components/DateTimeDisplay';
import { useRequiredParams, useTableParams } from '~/core/shared/hooks';
import { DocumentMessage, FilterOptions, Sorting } from '~/core/shared/types';
import sanitiseHtml from '~/core/shared/utils/sanitiseHtml';
import { ApplicationContext } from '../application';
import { MessagesTabContext } from '../application/tabs';
import { ReactQueryTable } from '../table';
import { EmailCreateModal } from './EmailCreateModal/EmailCreateModal';
import { MessageViewDrawer } from './MessageViewDrawer.component';
import { NoteCreateModal } from './NoteCreateModal.component';

const getColumns = (
  setMessage: (newMessage: DocumentMessage) => void,
  messageViewDrawerApi: UseOpenCloseReturn,
): ColumnDef<DocumentMessage>[] => [
  {
    accessorKey: 'createdAt',
    cell({ row }) {
      return (
        <Text>
          <DateTimeDisplay value={row.original.createdAt} />
        </Text>
      );
    },
    header: 'Date',
  },
  {
    accessorKey: 'createdBy',
    cell({ row }) {
      return <Text>{row.original.createdBy}</Text>;
    },
    header: 'User',
  },
  {
    accessorKey: 'category',
    cell({ row }) {
      return <Text textTransform="capitalize">{row.original.category}</Text>;
    },
    header: 'Category',
  },
  {
    accessorKey: 'private',
    cell({ row }) {
      return <Text>{row.original.private ? 'Yes' : 'No'}</Text>;
    },
    header: 'Private',
  },
  {
    accessorKey: 'subject',
    cell({ row }) {
      return <Text>{truncate(row.original.subject)}</Text>;
    },
    header: 'Subject',
  },
  {
    accessorKey: 'content',
    cell({ row }) {
      return <>{sanitiseHtml(truncate(row.original.content))}</>;
    },
    enableSorting: false,
    header: 'Content',
  },
  {
    accessorKey: 'id',
    cell({ row }) {
      return (
        <Stack
          alignItems="center"
          cursor="pointer"
          direction="row"
          onClick={() => {
            setMessage(row.original);
            messageViewDrawerApi.onOpen();
          }}
        >
          <Icon as={AiOutlineEye} boxSize="18px" />
          <Text>View</Text>
        </Stack>
      );
    },
    enableSorting: false,
    header: 'View',
  },
];

const defaultSorting: Sorting = { column: 'updated_at', direction: 'desc' };

export const ApplicationMessagesTable: FC = () => {
  const { uuid } = useRequiredParams(['uuid']);
  const { definition } = useContext(ApplicationContext);
  const [message, setMessage] = useState<DocumentMessage>();
  const messageViewDrawerApi = useOpenClose();
  const { filterOptions } = useContext(MessagesTabContext);

  const additionalParams = useMemo(() => {
    if (
      filterOptions.private &&
      (filterOptions as FilterOptions<DocumentMessage>).private !== 'all'
    ) {
      return { private: filterOptions.private === 'private' };
    }
    return undefined;
  }, [filterOptions]);

  const { tableProps, searchParams } = useTableParams(
    defaultSorting,
    25,
    additionalParams,
  );

  const { messages, meta, ...messagesQueryState } = useApplicationMessages(
    uuid,
    definition,
    searchParams as any,
  );

  const columns = useMemo(
    () => getColumns(setMessage, messageViewDrawerApi),
    [setMessage, messageViewDrawerApi],
  );

  return (
    <>
      <EmailCreateModal documentDefinition={definition} documentId={uuid} />
      <NoteCreateModal documentDefinition={definition} documentId={uuid} />
      {message && (
        <MessageViewDrawer drawerApi={messageViewDrawerApi} message={message} />
      )}
      {messages && (
        <ReactQueryTable<DocumentMessage>
          columns={columns}
          data={messages}
          isLoading={messagesQueryState.isFetching}
          meta={meta}
          {...tableProps}
        />
      )}
    </>
  );
};
